import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { AuthContext } from "./Auth/AuthContext";
import usePlans from "../hooks/usePlans";

const defaultState = {
  loading: true,
  // So fica true quando o plano de verdade chegou (API, cache ou super).
  // Enquanto for false ninguem pode bloquear rota por plano: sem dado, a
  // decisao seria "modulo desligado" e o usuario era expulso da tela.
  planLoaded: false,
  planActive: true,
  // canais
  whatsapp: true,
  whatsappOficial: false,
  facebook: true,
  instagram: true,
  metaAds: true,
  googleAds: true,
  googleBusiness: true,
  email: true,
  chat: true,
  internalChat: true,
  // módulos top-level
  crm: true,
  automacao: false,
  campaigns: false,
  vendas: true,
  marketing: false,
  financeiro: false,
  integrations: false,
  // módulos standalone
  agenda: true,
  servicos: true,
  projetos: true,
  tarefas: true,
  schedules: false,
  externalApi: false,
  // sub-flags legacy
  openAi: false,
  kanban: true,
  produtos: true,
  ordemDeServico: true,
  // limites numéricos (0 = ilimitado)
  maxConnections: 0,
  maxUsers: 0,
  maxQueues: 0,
};

const PlanPermissionsContext = createContext({
  ...defaultState,
  canAccess: () => false,
});

const SUPER_STATE = {
  loading: false, planLoaded: true, planActive: true,
  whatsapp: true, whatsappOficial: true, facebook: true, instagram: true,
  metaAds: true, googleAds: true, googleBusiness: true, email: true,
  chat: true, internalChat: true,
  crm: true, automacao: true, campaigns: true, vendas: true,
  marketing: true, financeiro: true, integrations: true,
  agenda: true, servicos: true, projetos: true, tarefas: true,
  schedules: true, externalApi: true,
  openAi: true, kanban: true, produtos: true, ordemDeServico: true,
  maxConnections: 0, maxUsers: 0, maxQueues: 0,
};

const CACHE_KEY = (companyId) => `plan_perms_v1_${companyId}`;

const parsePlanToState = (plan, user) => {
  const isExpired = moment().isBefore(user?.company?.dueDate);
  const planActive = user?.company?.status ? true : isExpired;
  // Fallbacks para compatibilidade com planos antigos (antes da migration)
  const financeiro = plan.useFinanceiro !== undefined
    ? Boolean(plan.useFinanceiro)
    : Boolean(plan.useIntegrations);
  const automacao = plan.useAutomacao !== undefined
    ? Boolean(plan.useAutomacao)
    : Boolean(plan.useOpenAi);
  const crm = plan.useCrm !== undefined
    ? plan.useCrm !== false
    : plan.useKanban !== false;
  return {
    loading: false,
    planLoaded: true,
    planActive,
    // canais
    whatsapp:        plan.useWhatsapp        !== false,
    whatsappOficial: Boolean(plan.useWhatsappOficial),
    metaEstrutura:   Boolean(plan.useMetaEstrutura),
    facebook:        plan.useFacebook        !== false,
    instagram:       plan.useInstagram       !== false,
    metaAds:         plan.useMetaAds         !== false,
    googleAds:       plan.useGoogleAds       !== false,
    googleBusiness:  plan.useGoogleBusiness  !== false,
    email:           plan.useEmail           !== false,
    chat:            plan.useChat            !== false,
    internalChat:    plan.useInternalChat    !== false,
    // módulos top-level
    crm,
    automacao,
    campaigns:    Boolean(plan.useCampaigns),
    vendas:       plan.useVendas   !== undefined ? plan.useVendas   !== false : true,
    marketing:    Boolean(plan.useMarketing),
    financeiro,
    integrations: Boolean(plan.useIntegrations),
    // módulos standalone
    agenda:         plan.useAgenda        !== false,
    servicos:       plan.useServicos      !== false,
    projetos:       plan.useProjetos      !== false,
    tarefas:        plan.useTarefas       !== undefined ? plan.useTarefas !== false : true,
    schedules:      Boolean(plan.useSchedules),
    externalApi:    Boolean(plan.useExternalApi),
    // sub-flags legacy
    openAi:         Boolean(plan.useOpenAi),
    kanban:         plan.useKanban        !== false,
    produtos:       plan.useProdutos      !== false,
    ordemDeServico: plan.useOrdemDeServico !== false,
    // limites
    maxConnections: Number(plan.connections) || 0,
    maxUsers:       Number(plan.users)       || 0,
    maxQueues:      Number(plan.queues)      || 0,
  };
};

// Lê plano do cache localStorage para eliminar race condition no F5.
// Retorna null se não houver cache válido.
const readPlanCache = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const raw = token.startsWith('"') ? JSON.parse(token) : token;
    const parts = raw.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1]));
    const companyId = payload.companyId;
    if (!companyId) return null;
    const cached = localStorage.getItem(CACHE_KEY(companyId));
    if (!cached) return null;
    return JSON.parse(cached);
  } catch (e) {
    return null;
  }
};

const savePlanCache = (companyId, planState) => {
  try {
    localStorage.setItem(CACHE_KEY(companyId), JSON.stringify(planState));
  } catch (e) {}
};

export const PlanPermissionsProvider = ({ children }) => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  // Inicializa com cache do localStorage para evitar redirect indevido no F5.
  // Se tiver cache, loading:false imediatamente — a API atualiza em background.
  const [state, setState] = useState(() => {
    const cached = readPlanCache();
    if (cached) return { ...cached, loading: false };
    return defaultState; // loading: true — aguarda API
  });

  useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      if (authLoading) return;

      if (!user?.companyId) {
        setState({ ...defaultState, loading: false });
        return;
      }

      if (user.companyId === 1) {
        setState(SUPER_STATE);
        return;
      }

      // Só mostra loading se não tiver cache (primeira vez)
      setState(prev => ({ ...prev, loading: prev.loading }));

      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (!isMounted) return;

        if (planConfigs?.plan) {
          const newState = parsePlanToState(planConfigs.plan, user);
          setState(newState);
          savePlanCache(user.companyId, newState);
        } else {
          setState({ ...defaultState, loading: false });
        }
      } catch (err) {
        if (isMounted) setState(prev => ({ ...prev, loading: false }));
      }
    };

    loadPlan();
    return () => { isMounted = false; };
  }, [authLoading, user?.companyId, user?.company?.dueDate]);

  // Re-busca o plano quando o usuário retorna para a aba (admin pode ter alterado o plano)
  useEffect(() => {
    if (!user?.companyId) return;

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        if (user.companyId === 1) { setState(SUPER_STATE); return; }
        getPlanCompany(undefined, user.companyId)
          .then((planConfigs) => {
            if (planConfigs?.plan) {
              const newState = parsePlanToState(planConfigs.plan, user);
              setState(newState);
              savePlanCache(user.companyId, newState);
            }
          })
          .catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [user?.companyId, user?.company?.dueDate]);

  const value = useMemo(() => {
    const canAccess = (featureKey) => {
      if (!featureKey) return true;
      return Boolean(state[featureKey]);
    };
    return { ...state, canAccess };
  }, [state]);

  return (
    <PlanPermissionsContext.Provider value={value}>
      {children}
    </PlanPermissionsContext.Provider>
  );
};

export const usePlanPermissions = () => useContext(PlanPermissionsContext);

export default PlanPermissionsContext;
